import { CellClassParams, ColDef, ICellRendererParams } from 'ag-grid-community';
import { CorpSegmentsEntity } from 'src/models/AppContextModels';
import { ForecastGridFixedFields, ColumnWidths } from '../../ForecastGridConstants';
import LineItemIdCellRenderer from '../../LineItemIdCellRenderer';
import { customLineItemIdComparator } from '../../ForecastTemplateUtils';

/**
 * Type for corp segment default values mapping
 * Maps corp segment name to its default value
 */
export interface CorpSegmentDefaultValues {
  [segmentName: string]: string;
}

/**
 * Extracts default values from corp segments for non-mandatory fields
 * @param corpSegments Array of corp segments from business group
 * @returns Map of segment name to default value
 */
export const getCorpSegmentDefaultValues = (corpSegments: CorpSegmentsEntity[]): CorpSegmentDefaultValues => {
  return corpSegments.reduce((acc, corpSegment) => {
    // Only get default values for non-required (optional) segments
    if (!corpSegment.corp_segment_required && corpSegment.corp_segment_default_value?.segment_hierarchy?.[0]) {
      acc[corpSegment.corp_segment_name] = corpSegment.corp_segment_default_value.segment_hierarchy[0];
    }
    return acc;
  }, {} as CorpSegmentDefaultValues);
};

/**
 * Prepares the initial line item ID column for the forecast grid.
 * This column is used to display the line item ID and also serves as the checkbox selection column.
 *
 * @param isCycleLocked Whether the planning cycle is locked
 * @param corpSegments Optional array of corp segments for extracting default values
 * @returns The column definition for the line item ID column
 */
export const prepareInitialLineItemIdColumn = (isCycleLocked: boolean, corpSegments?: CorpSegmentsEntity[]): ColDef => {
  // Get default values for optional corp segments
  const corpSegmentDefaultValues = corpSegments ? getCorpSegmentDefaultValues(corpSegments) : {};

  const lineItemIdColumnDefault: ColDef = {
    field: ForecastGridFixedFields.XptLineItemId.value,
    headerName: ForecastGridFixedFields.XptLineItemId.displayName,
    pinned: 'left',
    lockPinned: true,
    headerClass: ['text-center'],
    wrapHeaderText: true,
    hide: false,
    width: ColumnWidths.LINE_ITEM_ID,
    cellDataType: 'number',
    comparator: (valueA: string, valueB: string, nodeA: any, nodeB: any, isDescending: boolean) => {
      return customLineItemIdComparator(valueA, valueB, nodeA, nodeB);
    },
    cellClass: ['text-field'],
    cellClassRules: {
      'bold-text': (params: CellClassParams) => params.node.footer || false
    },
    cellRenderer: LineItemIdCellRenderer,
    cellRendererParams: {
      corpSegmentDefaultValues
    }
  };

  // Show checkboxes only for users with edit permissions and when the planning cycle is unlocked
  // Note: checkboxes are now controlled at grid level via rowSelection.checkboxes
  // isRowSelectable in the grid's rowSelection config handles row-level authorization

  return lineItemIdColumnDefault;
};

/**
 * Generates metadata columns for the forecast grid.
 * These columns are typically hidden in production but visible in development for debugging purposes.
 *
 * @returns An array of column definitions for metadata columns
 */
export const metadataColumns = (): ColDef[] => {
  // Common properties shared across metadata columns
  const metadataColumnProps: Partial<ColDef> = {
    pinned: 'left',
    lockPinned: true,
    headerClass: ['text-center'],
    wrapHeaderText: true,
    hide: true,
    suppressColumnsToolPanel: true,
    suppressFiltersToolPanel: true,
    width: 100,
    minWidth: 100,
    cellDataType: 'text',
    cellClassRules: {
      'bold-text': (params: CellClassParams) => params.node.footer || false
    }
  };

  // Metadata columns definition using spread syntax to combine common properties
  const metadataColumns: ColDef[] = [
    {
      field: ForecastGridFixedFields.IsNew.value,
      headerName: ForecastGridFixedFields.IsNew.displayName,
      ...metadataColumnProps
    },
    {
      field: ForecastGridFixedFields.IsSegmentEdited.value,
      headerName: ForecastGridFixedFields.IsSegmentEdited.displayName,
      ...metadataColumnProps
    },
    {
      field: ForecastGridFixedFields.IsNewFERow.value,
      headerName: ForecastGridFixedFields.IsNewFERow.displayName,
      ...metadataColumnProps
    },
    {
      field: ForecastGridFixedFields.IsEdited.value,
      headerName: ForecastGridFixedFields.IsEdited.displayName,
      ...metadataColumnProps
    }
  ];

  return metadataColumns;
};
